import { Image, Tooltip } from 'antd';
import {
  ApplyProduct,
  ApplyProductProps,
} from 'components/Dialogue/components/QAPair/ApplyProduct';
import { useQueryQAPairProduct } from 'components/Dialogue/components/QAPair/useQAPairProduct';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  SupplementKnowledgeData,
  SupplementKnowledgeRelatedSPU,
} from 'services/api/supplementKnowledge';
import { SupplementKnowledgeDetailResponse } from 'services/api/supplementKnowledge';
import { useIsExpandInCalculation, useUpdateSeekSku } from '../useUpdateSeekSku';
import { formatNumber } from 'utils/numberFormat';
import { MenuFoldOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { ExpandSkuDrawer } from '../SpuMapping/ExpandSkuDrawer';
import useModal from 'hooks/useModal';

interface ProductInfoProps {
  refreshKey?: number;
  foldEnable?: boolean;
  dataSource: {
    expandCount?: SupplementKnowledgeData['expandCount'];
    storeInfo?: Pick<
      Required<SupplementKnowledgeData>['storeInfo'],
      'externalType' | 'region' | 'storeId'
    >;
  };
  knowledgeId: SupplementKnowledgeData['id'];
  submitItemIdentifier: SupplementKnowledgeRelatedSPU;
  skuIds: SupplementKnowledgeData['supplementalKnowledgeValue']['skuIds'];
  onRefresh?: () => void;
  onDelayRefresh?: () => void;
  onFold?: () => void;
  onUpdate?: (info: { spuId: string; skuIds: string[] }) => void;
  extra?: JSX.Element;
  showExpandSkuButton?: boolean;
}

const Apply = (
  props: Pick<ApplyProductProps, 'defaultApplyType' | 'imageUrl' | 'skuInfo' | 'selectable'> & {
    knowledgeId: SupplementKnowledgeData['id'];
    submitItemIdentifier: SupplementKnowledgeRelatedSPU;
    skuIds: SupplementKnowledgeDetailResponse['supplementalKnowledgeValue']['skuIds'];
    onRefresh?: () => void;
    onUpdate?: (info: { spuId: string; skuIds: string[] }) => void;
  },
) => {
  const {
    knowledgeId,
    submitItemIdentifier,
    skuIds = [],
    defaultApplyType,
    imageUrl,
    skuInfo,
    selectable,
    onRefresh,
    onUpdate,
  } = props;
  const { spuId } = submitItemIdentifier;

  const [selectedSkus, setSelectedSkus] = useState<string[]>(
    defaultApplyType === 'link' ? skuInfo?.map((sku) => sku.skuId) || [] : skuIds,
  );

  const paramsStrRef = useRef<string>('');

  const { updateAsync: updateSeedSkuAsync } = useUpdateSeekSku({ onRefresh });

  useLayoutEffect(() => {
    paramsStrRef.current = JSON.stringify({
      knowledgeId,
      spuId,
      skuIds: selectedSkus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ApplyProduct
      v2
      imageUrl={imageUrl}
      skuInfo={skuInfo}
      defaultApplyType={defaultApplyType}
      showImage={false}
      skuSelected={selectedSkus}
      selectable={selectable}
      onSKUChange={setSelectedSkus}
      size="small"
      onHide={() => {
        const currentParamsStr = JSON.stringify({
          knowledgeId,
          spuId,
          skuIds: selectedSkus,
        });
        if (currentParamsStr !== paramsStrRef.current) {
          if (onUpdate) {
            onUpdate({
              spuId,
              skuIds: selectedSkus,
            });
          } else {
            updateSeedSkuAsync({
              knowledgeId,
              spuId,
              skuIds: selectedSkus,
            });
          }
        }
      }}
    />
  );
};

export const ExpandSkuButton = (props: {
  spuCount: number;
  skuCount: number;
  knowledgeId: SupplementKnowledgeData['id'];
  onRefresh?: () => void;
}) => {
  const { spuCount, skuCount, knowledgeId, onRefresh } = props;
  const { t } = useTranslate();
  const {
    visible: expandSkuVisible,
    meta: expandSkuKnowledgeId,
    hide: hideExpandSku,
    show: showExpandSku,
  } = useModal<SupplementKnowledgeData['id']>();

  useEffect(() => {
    if (!expandSkuVisible) {
      onRefresh?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandSkuVisible]);

  return (
    <>
      <span className="cursor-pointer" onClick={() => showExpandSku(knowledgeId)}>
        {t('扩展同款商品xspuysku', {
          x: spuCount,
          y: skuCount,
        })}
        <RightOutlined />
      </span>
      {!!expandSkuKnowledgeId && (
        <ExpandSkuDrawer
          knowledgeId={expandSkuKnowledgeId}
          visible={expandSkuVisible}
          onClose={() => {
            hideExpandSku();
          }}
        />
      )}
    </>
  );
};

export const ProductInfo = (props: ProductInfoProps) => {
  const {
    foldEnable = true,
    submitItemIdentifier,
    dataSource,
    knowledgeId,
    skuIds,
    onRefresh,
    onDelayRefresh,
    onFold,
    onUpdate,
    extra,
    showExpandSkuButton = true,
  } = props;
  const { storeInfo, expandCount } = dataSource;
  const { storeId, externalType, region } = storeInfo || {};
  const { spuId, imageUrl, title, price, unit } = submitItemIdentifier;
  const { t } = useTranslate();
  const isExpandInCalculation = useIsExpandInCalculation();

  const { isLoading, product, selectable, defaultApplyType } = useQueryQAPairProduct({
    enable: true,
    itemId: spuId,
    storeId: storeId?.toString(),
    channel: externalType as any,
    region: region as any,
    selectedSkus: skuIds,
  });
  const itemInfo = product?.item;

  return (
    <div className="bg-[var(--fett-knowledge-product-card-bg)] p-[10px] rounded-sm flex items-center gap-2">
      <Image width={60} height={60} src={imageUrl} alt="product" />
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-4">
          <div className="!my-0 !font-medium flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
            {title}
          </div>
          {foldEnable && (
            <Tooltip title={t('将聊天记录收起')}>
              <div className="cursor-pointer hover:opacity-80" onClick={() => onFold?.()}>
                <MenuFoldOutlined />
              </div>
            </Tooltip>
          )}
        </div>
        <div className="text-[var(--fett-text-tertiary)] text-[12px]">{`${unit} ${formatNumber(
          price,
          '0,0.00',
        )}`}</div>
        {extra || (
          <div className="flex w-full items-center justify-end">
            {showExpandSkuButton &&
              expandCount && [
                <div className="text-[var(--fett-scenario-accent-text)]">
                  {isExpandInCalculation(knowledgeId) ? (
                    t('计算中')
                  ) : (
                    <ExpandSkuButton
                      knowledgeId={knowledgeId}
                      spuCount={expandCount?.spuCount ?? 0}
                      skuCount={expandCount?.skuCount ?? 0}
                      onRefresh={onRefresh}
                    />
                  )}
                </div>,
                <div className="w-[1px] h-[14px] bg-[#336fff] mx-1" />,
              ]}
            {!isLoading && itemInfo && defaultApplyType && (
              <Apply
                knowledgeId={knowledgeId}
                submitItemIdentifier={submitItemIdentifier}
                skuIds={skuIds}
                imageUrl={imageUrl}
                skuInfo={itemInfo?.skuInfo}
                selectable={selectable}
                defaultApplyType={defaultApplyType}
                onRefresh={onDelayRefresh}
                onUpdate={onUpdate}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
